import { PropsWithChildren } from 'react'
import { ErrorLevel, UIErrorCode } from '../constants'
import { AlertErrorFallback } from './alert-error-fallback'
import { CompactErrorFallback } from './compact-error-fallback'
import { FullscreenErrorFallback } from './fullscreen-error-fallback'
import type { PlatformError } from '../utils'

export interface ErrorFallbackProps<LevelOptions = ErrorLevel> {
  /**
   * The thrown Error object
   */
  error: PlatformError | Error
  /**
   * Callback to reset the error boundary state and try again
   */
  reset: () => void
  /**
   * The error level to render a fallback UI for. The level dictates how much of
   * the application is no longer functioning correctly and should be unmounted.
   */
  level?: LevelOptions
  /**
   * A trace ID for the error if available
   */
  traceId?: string
  /**
   * An error code, usually describing known errors which we have documented
   */
  code?: UIErrorCode | string
  /**
   * An email address to signpost users to when they need further support
   */
  supportEmail?: string
}

/**
 * Renders a fallback UI based on the level an error was caught. Error levels
 * usually correlate with severity, and error boundaries in the application.
 */
export const ErrorFallback = ({
  level: maybeStringLevel = ErrorLevel.COMPONENT,
  code = UIErrorCode.UNKNOWN,
  children,
  ...props
}: PropsWithChildren<ErrorFallbackProps<ErrorLevel | string>>) => {
  const level =
    typeof maybeStringLevel === 'string'
      ? (parseInt(maybeStringLevel, 10) as ErrorLevel)
      : maybeStringLevel

  switch (level) {
    case ErrorLevel.HOST:
    case ErrorLevel.TEMPLATE:
      return <FullscreenErrorFallback level={level} code={code} {...props} />

    case ErrorLevel.COMPONENT:
      return <CompactErrorFallback level={level} code={code} {...props} />

    case ErrorLevel.OBJECT:
    case ErrorLevel.GROUP:
      return (
        <AlertErrorFallback level={level} code={code} {...props}>
          {children}
        </AlertErrorFallback>
      )

    default:
      return null
  }
}
